// src/hooks/useCamera.ts

import { useCallback, useState } from 'react'
import cameraApiService from '../api/camera.api'
import { CameraQueryParams } from '../types/camera-query-parameters'
import { Camera } from 'domain/camera'
import { useToast } from 'layout/context/toast-context'
import { AxiosError } from 'axios'
import { ApiResponse } from 'http/types/api-response'
import { ApiErrorResponse } from 'http/types/api-error-response'

interface UseCameraHookReturn {
  // List operations
  cameras: Camera[]
  totalCameras: number
  currentPage: number
  pageSize: number
  totalPages: number
  isLoading: boolean
  error: Error | null

  // Actions
  fetchCameras: (params?: CameraQueryParams) => Promise<void>
  createCamera: (data: Camera) => Promise<Camera | null>
  updateCamera: (id: string, data: Camera) => Promise<Camera | null>
  deleteCamera: (id: string) => Promise<boolean>
}

export const useCamera = (): UseCameraHookReturn => {
  const { showSuccess, showError } = useToast()
  const [cameras, setCameras] = useState<Camera[]>([])
  const [totalCameras, setTotalCameras] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(10)
  const [totalPages, setTotalPages] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchCameras = useCallback(
    async (params?: CameraQueryParams): Promise<void> => {
      // Only proceed if params has clientId
      if (!params?.clientId) {
        return
      }

      setIsLoading(true)
      setError(null)

      await cameraApiService.getCameras(params)
        .then((response) => {
          setCameras(response.responseObject)
          setTotalCameras(response.total)
          setCurrentPage(response.page)
          setPageSize(response.pageSize)
          setTotalPages(response.totalPages)
        })
        .catch((error: AxiosError) => {
          const err = error.response?.data as ApiErrorResponse | undefined

          if (err?.error && err?.message) {
            showError(err.error, err.message)
          } else if (!error.response) {
            showError('Erro de conexão', 'Serviço temporariamente indisponível')
          }
          setError(error as Error)
          setCameras([])
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [showError]
  )

  const createCamera = useCallback(
    async (camera: Camera): Promise<Camera | null> => {

      setIsLoading(true)
      const response = await cameraApiService.createCamera(camera)
        .then((data: ApiResponse<Camera>) => {
          const newCamera = data.responseObject as Camera
          if (newCamera.clientId === camera.clientId) {
            fetchCameras({ clientId: camera.clientId })
          }

          showSuccess('Sucesso', `Câmera ${newCamera.name} criada com sucesso`)

          return newCamera
        }).catch((error: AxiosError) => {
          const err = error.response?.data as ApiErrorResponse | undefined

          if (err?.error && err?.message) {
            showError(err.error, err.message)
          } else if (!error.response) {
            showError('Erro de conexão', 'Serviço temporariamente indisponível')
          }
          return null
        }).finally(() => {
          setIsLoading(false)
        })

      return response
    },
    [fetchCameras, showSuccess, showError]
  )

  const updateCamera = useCallback(
    async (cameraId: string, camera: Camera): Promise<Camera | null> => {

      setIsLoading(true)
      const response = await cameraApiService.updateCamera(camera)
        .then((data: ApiResponse<Camera>) => {
          const updatedCamera = data.responseObject as Camera

          // Update the local state to reflect changes
          setCameras((prevCameras) =>
            prevCameras.map((cam) =>
              cam.id === cameraId ? updatedCamera : cam
            )
          )

          showSuccess('Sucesso', `Câmera ${updatedCamera.name} atualizada com sucesso`)

          return updatedCamera
        }).catch((error: AxiosError) => {
          const err = error.response?.data as ApiErrorResponse | undefined

          if (err?.error && err?.message) {
            showError(err.error, err.message)
          } else if (!error.response) {
            showError('Erro de conexão', 'Serviço temporariamente indisponível')
          }
          setError(error as Error)
          return null
        }).finally(() => {
          setIsLoading(false)
        })

      return response
    },
    [showSuccess, showError]
  )

  const deleteCamera = useCallback(async (id: string): Promise<boolean> => {

    setIsLoading(true)
    const response = await cameraApiService.deleteCamera(id)
      .then(() => {
        setCameras((prevCameras) =>
          prevCameras.filter((camera) => camera.id !== id)
        )

        showSuccess('Sucesso', 'Câmera excluída com sucesso')
        return true
      }).catch((error: AxiosError) => {
        const err = error.response?.data as ApiErrorResponse

        if (err.error && err.message) {
          showError(err.error, err.message)
        }
        setError(error as Error)
        return false
      }).finally(() => {
        setIsLoading(false)
      })

    return response
  }, [showSuccess, showError])

  return {
    cameras,
    totalCameras,
    currentPage,
    pageSize,
    totalPages,
    isLoading,
    error,
    fetchCameras,
    createCamera,
    updateCamera,
    deleteCamera,
  }
}
